import React, { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { Table, Badge, Button, Spinner, Dropdown, Form, Row, Col, Modal, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { usePaginatedList } from '../../hooks/usePaginatedList';
import PaginationControls from '../commons/PaginationControls';
import { sendRecoverPassword } from '../../api/user';

const SORT = { nombre: 1 };
const INITIAL_FILTERS = { search: '', identificacion: '', ciudad: '', contacto: '' };

const STATUS_BADGE = {
  activo:     { bg: 'success',   label: 'Activo' },
  'no activo':{ bg: 'warning',   label: 'No activo' },
  eliminado:  { bg: 'danger',    label: 'Eliminado' },
};

const getStatusBadge = (status) => {
  const s = (status || '').toLowerCase();
  const cfg = STATUS_BADGE[s] || { bg: 'secondary', label: status || 'Desconocido' };
  return <Badge bg={cfg.bg}>{cfg.label}</Badge>;
};

const ClientesTable = forwardRef(({
  roleId,
  onSyncIndividual,
  syncIndividualLoading,
  onEdit,
  onDelete,
}, ref) => {
  const navigate = useNavigate();
  const [activationUsuario, setActivationUsuario] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);

  const buildConditions = useMemo(() => (filters) => ({
    role: roleId,
    ...(filters.search        ? { nombre:        { $regex: filters.search,        $options: 'i' } } : {}),
    ...(filters.identificacion? { identificacion: { $regex: filters.identificacion,$options: 'i' } } : {}),
    ...(filters.ciudad        ? { ciudad:         { $regex: filters.ciudad,        $options: 'i' } } : {}),
    ...(filters.contacto      ? { contacto:       { $regex: filters.contacto,      $options: 'i' } } : {}),
  }), [roleId]);

  const {
    results,
    pagination,
    loading,
    error,
    filters,
    updateFilter,
    resetFilters,
    changePage,
    changePerPage,
    refresh,
    pendingUpdates,
  } = usePaginatedList({
    endpoint: '/usuarios/paginar',
    initialFilters: INITIAL_FILTERS,
    initialPerPage: 20,
    sort: SORT,
    buildConditions,
  });

  useImperativeHandle(ref, () => ({ refresh }));

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleSendActivation = async () => {
    if (!activationUsuario) return;
    setSendingEmail(true);
    try {
      await sendRecoverPassword({ email: activationUsuario.correo });
      setActivationUsuario(null);
      Swal.fire({ title: 'Correo enviado', text: `Se envió a ${activationUsuario.correo}`, icon: 'success', toast: true, position: 'top-end', timer: 3000, showConfirmButton: false });
    } catch (err) {
      const msg = err.response?.data?.message || err.message;
      Swal.fire('Error', msg, 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const COLSPAN = 9;

  return (
    <div>
      {/* Filtros */}
      <Row className="mb-3 g-2">
        <Col md={3} sm={6}>
          <Form.Control
            type="text"
            placeholder="Buscar por nombre..."
            value={filters.search || ''}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <Form.Control
            type="text"
            placeholder="Identificación..."
            value={filters.identificacion || ''}
            onChange={(e) => updateFilter('identificacion', e.target.value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <Form.Control
            type="text"
            placeholder="Ciudad..."
            value={filters.ciudad || ''}
            onChange={(e) => updateFilter('ciudad', e.target.value)}
          />
        </Col>
        <Col md={3} sm={6}>
          <Form.Control
            type="text"
            placeholder="Persona de contacto..."
            value={filters.contacto || ''}
            onChange={(e) => updateFilter('contacto', e.target.value)}
          />
        </Col>
      </Row>

      {/* Error */}
      {error && (
        <Alert variant="danger" className="d-flex align-items-center justify-content-between">
          <span><strong>Error al cargar clientes:</strong> {error}</span>
          <Button variant="outline-danger" size="sm" onClick={refresh}>Reintentar</Button>
        </Alert>
      )}

      {/* Tabla */}
      <Table striped bordered hover responsive size="sm">
        <thead>
          <tr>
            <th>Sync</th>
            <th>Nombre</th>
            <th>Identificación</th>
            <th className="d-none d-lg-table-cell">Email</th>
            <th>Ciudad</th>
            <th className="d-none d-md-table-cell">Contacto</th>
            <th className="d-none d-md-table-cell">Teléfono</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr>
              <td colSpan={COLSPAN} className="text-center py-4">
                <Spinner animation="border" size="sm" /> Cargando...
              </td>
            </tr>
          ) : results.length === 0 ? (
            <tr>
              <td colSpan={COLSPAN} className="text-center py-4">
                <Alert variant="info" className="mb-0 d-inline-flex flex-column align-items-center">
                  <i className="bi bi-inbox fs-1 d-block mb-2" />
                  <p className="mb-2">No se encontraron clientes con los filtros seleccionados.</p>
                  <Button variant="outline-info" size="sm" onClick={resetFilters}>Limpiar filtros</Button>
                </Alert>
              </td>
            </tr>
          ) : (
            results.map((usuario) => (
              <tr key={usuario._id}>
                <td className="text-center">
                  {syncIndividualLoading === usuario._id ? (
                    <Spinner animation="border" size="sm" />
                  ) : usuario.contabilidad?.id ? (
                    <Badge
                      bg="success"
                      title={`Sync: ${formatDate(usuario.contabilidad.lastSync)} — ${usuario.contabilidad.providerName || ''}`}
                    >
                      ✅
                    </Badge>
                  ) : (
                    <Button variant="outline-secondary" size="sm" onClick={() => onSyncIndividual(usuario._id)}>
                      ⟳
                    </Button>
                  )}
                </td>
                <td>
                  <span
                    style={{ cursor: 'pointer', color: '#0d6efd', textDecoration: 'underline' }}
                    onClick={() => navigate(`/perfil?_id=${usuario._id}`)}
                  >
                    {usuario.nombre} {usuario.apellido || ''}
                  </span>
                </td>
                <td>{usuario.identificacion}</td>
                <td className="d-none d-lg-table-cell">{usuario.correo}</td>
                <td>{usuario.ciudad}</td>
                <td className="d-none d-md-table-cell">{usuario.contacto}</td>
                <td className="d-none d-md-table-cell">{usuario.telefono}</td>
                <td>{getStatusBadge(usuario.status)}</td>
                <td>
                  <Dropdown>
                    <Dropdown.Toggle variant="light" size="sm" id={`dd-${usuario._id}`}>
                      ⋮
                    </Dropdown.Toggle>
                    <Dropdown.Menu>
                      <Dropdown.Item onClick={() => onEdit(usuario)}>Ver / Editar</Dropdown.Item>
                      <Dropdown.Item onClick={() => setActivationUsuario(usuario)}>
                        ✉ Enviar correo de activación
                      </Dropdown.Item>
                      <Dropdown.Divider />
                      <Dropdown.Item onClick={() => onDelete(usuario)} className="text-danger">
                        Eliminar
                      </Dropdown.Item>
                    </Dropdown.Menu>
                  </Dropdown>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>

      {/* Paginación */}
      <PaginationControls
        {...pagination}
        onPageChange={changePage}
        onPerPageChange={changePerPage}
        onRefresh={refresh}
        loading={loading}
        pendingUpdates={pendingUpdates}
      />

      {/* Modal confirmación envío correo */}
      <Modal show={!!activationUsuario} onHide={() => setActivationUsuario(null)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title>Enviar correo de activación</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-1">Se enviará el correo a:</p>
          <p className="fw-bold">{activationUsuario?.correo}</p>
          <p className="text-muted small mb-0">Cliente: {activationUsuario?.nombre} {activationUsuario?.apellido || ''}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setActivationUsuario(null)}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleSendActivation} disabled={sendingEmail}>
            {sendingEmail ? <Spinner animation="border" size="sm" /> : 'Enviar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

ClientesTable.displayName = 'ClientesTable';

export default ClientesTable;
